import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, tap, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  LocationInventorySummary,
  DirectUpdateRequest,
  TransferRequest,
  PendingTransfer,
  PendingTransfersResponse,
  ConfirmTransferRequest,
  InventoryApiResponse,
  InventorySummaryResponse,
  MapOverviewResponse,
  ForecastResponse
} from '../models';

/**
 * MatInventoryService
 * 
 * Handles all inventory management API calls to the .NET Lambda backend.
 * The MSAL interceptor automatically adds the Authorization header for all
 * requests to the configured API base URL.
 * 
 * State Management:
 * - Maintains a BehaviorSubject for summary data to enable automatic refresh
 * - All POST operations trigger a refresh of the summary data
 */
@Injectable({
  providedIn: 'root'
})
export class MatInventoryService {
  private readonly baseUrl = environment.apiBaseUrl;
  
  // State management for full summary response
  private summaryResponseSubject = new BehaviorSubject<InventorySummaryResponse | null>(null);
  public summaryResponse$ = this.summaryResponseSubject.asObservable();
  
  // State management for summary data (legacy)
  private summarySubject = new BehaviorSubject<LocationInventorySummary[]>([]);
  public summary$ = this.summarySubject.asObservable();

  // State management for pending transfers
  private pendingTransfersSubject = new BehaviorSubject<PendingTransfer[]>([]);
  public pendingTransfers$ = this.pendingTransfersSubject.asObservable();

  // State management for pending transfers pagination metadata
  private pendingTransfersPageSubject = new BehaviorSubject<PendingTransfersResponse | null>(null);
  public pendingTransfersPage$ = this.pendingTransfersPageSubject.asObservable();

  /** Last page/pageSize requested — used when refreshing after a POST action */
  private lastPendingTransfersPage = 1;
  private lastPendingTransfersPageSize = 5;
  private lastPendingTransfersStatus: 'current' | 'completed' | 'rejected' = 'current';

  constructor(private http: HttpClient) {}

  /**
   * GET /getsummary
   * Retrieves complete inventory summary including KPIs, storage hubs, and location summaries
   */
  getSummary(): Observable<InventorySummaryResponse> {
    return this.http.get<InventorySummaryResponse>(`${this.baseUrl}/getsummary`)
      .pipe(
        tap(data => {
          this.summaryResponseSubject.next(data);
          // For backwards compatibility, also update the legacy subject
          // Note: You'll need to map LocationMaterialSummaries to LocationInventorySummary if needed
        }),
        catchError(this.handleError)
      );
  }

  /**
   * GET /getMapOverview
   * Retrieves map-specific data optimized for map visualization
   * Includes location coordinates, inventory levels, and map markers
   */
  getMapOverview(): Observable<MapOverviewResponse> {
    return this.http.get<MapOverviewResponse>(`${this.baseUrl}/getMapOverview`)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * GET /getForecasting
   * Retrieves demand forecast data for specified region and time horizon
   * @param region Region filter (ALL, DEC, DEF, DEM, DEP) - default: ALL
   * @param weeks Forecast horizon (4, 8, or 12 weeks) - default: 12
   * @param dateType Date field to bucket by: 'start_date' or 'laid_date' - default: 'start_date'
   */
  getForecasting(region: string = 'ALL', weeks: number = 12, dateType: string = 'start_date'): Observable<ForecastResponse> {
    return this.http.get<ForecastResponse>(`${this.baseUrl}/getForecasting`, {
      params: { region, weeks: weeks.toString(), dateType }
    })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * POST /directupdate
   * Performs manual inventory adjustments (Add or Remove)
   * Automatically refreshes summary data after successful update
   */
  directUpdate(request: DirectUpdateRequest): Observable<any> {
    return this.http.post(`${this.baseUrl}/directupdate`, request)
      .pipe(
        tap(() => this.refreshSummary()),
        catchError(this.handleError)
      );
  }

  /**
   * POST /requesttransfer
   * Initiates a transfer from source to target location
   * Moves inventory from Source-Available to Source-In_Transit
   * Automatically refreshes summary data after successful transfer
   */
  requestTransfer(request: TransferRequest): Observable<any> {
    return this.http.post(`${this.baseUrl}/requesttransfer`, request)
      .pipe(
        tap(() => {
          this.refreshSummary();
          this.refreshPendingTransfers();
        }),
        catchError(this.handleError)
      );
  }

  /**
   * GET /getpendingtransfers
   * Retrieves a server-side page of transfers with EventStatusCd == 'Initiated'
   * @param pageNumber 1-based page number — default 1
   * @param pageSize Number of rows per page — default 5
   */
  getPendingTransfers(pageNumber: number = 1, pageSize: number = 5, status: 'current' | 'completed' | 'rejected' = 'current'): Observable<PendingTransfersResponse> {
    this.lastPendingTransfersPage = pageNumber;
    this.lastPendingTransfersPageSize = pageSize;
    this.lastPendingTransfersStatus = status;
    return this.http.get<any>(`${this.baseUrl}/getpendingtransfers`, {
      params: { pageNumber: pageNumber.toString(), pageSize: pageSize.toString(), status }
    })
      .pipe(
        map(response => {
          let body = response;
          if (response && typeof response.body === 'string') {
            try { body = JSON.parse(response.body); } catch { body = {}; }
          }
          const transfers = body?.Transfers ?? body?.transfers ?? [];
          const mappedTransfers = Array.isArray(transfers) ? transfers.map((transfer: any) => ({
            ...transfer,
            VendorOrderStatusCd: transfer.VendorOrderStatusCd ?? transfer.vendorOrderStatusCd ?? null,
            VendorOrderId: transfer.VendorOrderId ?? transfer.vendorOrderId ?? null,
            VendorName: transfer.VendorName ?? transfer.vendorName ?? null
          })) : [];
          return {
            Transfers: mappedTransfers,
            PageNumber: body?.PageNumber ?? body?.pageNumber ?? pageNumber,
            PageSize: body?.PageSize ?? body?.pageSize ?? pageSize,
            TotalCount: body?.TotalCount ?? body?.totalCount ?? mappedTransfers.length,
            TotalPages: body?.TotalPages ?? body?.totalPages ?? 1,
            CurrentCount: body?.CurrentCount ?? body?.currentCount ?? 0,
            CompletedCount: body?.CompletedCount ?? body?.completedCount ?? 0,
            RejectedCount: body?.RejectedCount ?? body?.rejectedCount ?? 0
          } as PendingTransfersResponse;
        }),
        tap(page => {
          this.pendingTransfersSubject.next(page.Transfers);
          this.pendingTransfersPageSubject.next(page);
        }),
        catchError(this.handleError)
      );
  }

  /**
   * POST /confirmtransfer
   * Finalizes a transfer by moving inventory to Target-Available bucket
   * Automatically refreshes both summary and pending transfers after confirmation
   */
  confirmTransfer(request: ConfirmTransferRequest): Observable<any> {
    return this.http.post(`${this.baseUrl}/confirmtransfer`, request)
      .pipe(
        tap(() => {
          this.refreshSummary();
          this.refreshPendingTransfers();
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Refresh summary data (called internally after POST operations)
   */
  private refreshSummary(): void {
    this.getSummary().subscribe();
  }

  /**
   * Refresh pending transfers data (called internally after POST operations)
   * Reloads the currently-viewed page so confirming/creating a transfer
   * doesn't silently jump the user back to page 1.
   */
  private refreshPendingTransfers(): void {
    this.getPendingTransfers(this.lastPendingTransfersPage, this.lastPendingTransfersPageSize, this.lastPendingTransfersStatus).subscribe();
  }

  /**
   * Error handler for HTTP requests
   */
  private handleError(error: any): Observable<never> {
    console.error('Inventory API Error:', error);
    
    let errorMessage = 'An error occurred while processing your request.';
    
    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Server-side error
      errorMessage = error.error?.message || 
                     error.message || 
                     `Server returned code ${error.status}`;
    }
    
    return throwError(() => new Error(errorMessage));
  }
}
